import {
  UsersIcon,
  AlertTriangleIcon,
  FileTextIcon,
  Image as ImageIcon,
  LayoutTemplateIcon,
  TypeIcon,
  PaletteIcon,
} from 'lucide-react'

// Partner Data
export interface Partner {
  id: string
  name: string
  location: string
  tier: 'Platinum' | 'Gold' | 'Silver' | 'Bronze'
  performanceScore: number
  complianceScore: number
  status: 'Active' | 'Onboarding' | 'Suspended'
  leads: number
  spend: string
  roi: string
  lastAudit: string
}

export const PARTNERS: Partner[] = [
  {
    id: 'MH401',
    name: 'Mumbai Motors',
    location: 'Mumbai, MH',
    tier: 'Platinum',
    performanceScore: 92,
    complianceScore: 98,
    status: 'Active',
    leads: 20370,
    spend: '₹2.25M',
    roi: '4.2x',
    lastAudit: '2023-10-15',
  },
  {
    id: 'DL302',
    name: 'Delhi Auto Hub',
    location: 'New Delhi, DL',
    tier: 'Gold',
    performanceScore: 85,
    complianceScore: 90,
    status: 'Active',
    leads: 17150,
    spend: '₹1.89M',
    roi: '3.8x',
    lastAudit: '2023-10-10',
  },
  {
    id: 'HR407',
    name: 'Koncept Auto',
    location: 'Gurugram, HR',
    tier: 'Gold',
    performanceScore: 81,
    complianceScore: 85,
    status: 'Active',
    leads: 15280,
    spend: '₹1.68M',
    roi: '3.5x',
    lastAudit: '2023-09-28',
  },
  {
    id: 'MH201',
    name: 'Pune Motors',
    location: 'Pune, MH',
    tier: 'Silver',
    performanceScore: 76,
    complianceScore: 72,
    status: 'Active',
    leads: 13200,
    spend: '₹1.45M',
    roi: '2.9x',
    lastAudit: '2023-10-05',
  },
  {
    id: 'TN101',
    name: 'Chennai Cars',
    location: 'Chennai, TN',
    tier: 'Silver',
    performanceScore: 78,
    complianceScore: 88,
    status: 'Active',
    leads: 14850,
    spend: '₹1.52M',
    roi: '3.1x',
    lastAudit: '2023-10-12',
  },
]

// Campaign Templates
export interface CampaignTemplate {
  id: string
  name: string
  category:
    | 'Vehicle Launch'
    | 'Seasonal'
    | 'Service'
    | 'Exchange'
    | 'Brand Awareness'
  thumbnail: string
  channels: ('Google' | 'Facebook' | 'Instagram' | 'WhatsApp')[]
  duration: string
  budgetRange: string
  description: string
}

export const CAMPAIGN_TEMPLATES: CampaignTemplate[] = [
  {
    id: 'T001',
    name: 'New Scorpio-N Launch',
    category: 'Vehicle Launch',
    thumbnail:
      'https://images.unsplash.com/photo-1626847037657-fd3622613ce3?auto=format&fit=crop&q=80&w=600',
    channels: ['Google', 'Facebook', 'Instagram'],
    duration: '30-45 days',
    budgetRange: '₹50k - ₹2L',
    description:
      'Comprehensive launch kit for the new Scorpio-N including video ads, banners, and lead forms.',
  },
  {
    id: 'T002',
    name: 'Monsoon Service Camp',
    category: 'Service',
    thumbnail:
      'https://images.unsplash.com/photo-1632823471565-1ec2848bee7b?auto=format&fit=crop&q=80&w=600',
    channels: ['WhatsApp', 'Facebook'],
    duration: '15 days',
    budgetRange: '₹10k - ₹50k',
    description:
      'Promote monsoon check-up camps to existing customers via WhatsApp and social media.',
  },
  {
    id: 'T003',
    name: 'Festive Exchange Bonanza',
    category: 'Exchange',
    thumbnail:
      'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=600',
    channels: ['Google', 'Facebook'],
    duration: '30 days',
    budgetRange: '₹30k - ₹1L',
    description:
      'Drive exchange inquiries during the festive season with special bonus offers.',
  },
  {
    id: 'T004',
    name: 'XUV700 Test Drive',
    category: 'Brand Awareness',
    thumbnail:
      'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&q=80&w=600',
    channels: ['Facebook', 'Instagram'],
    duration: 'Ongoing',
    budgetRange: '₹20k - ₹80k',
    description:
      'Lead generation campaign focused on booking test drives for the XUV700.',
  },
  {
    id: 'T005',
    name: 'Year-End Stock Clearance',
    category: 'Seasonal',
    thumbnail:
      'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&q=80&w=600',
    channels: ['Google', 'Facebook', 'WhatsApp'],
    duration: '15-20 days',
    budgetRange: '₹40k - ₹1.5L',
    description:
      'Aggressive sales push for clearing year-end inventory with high discounts.',
  },
  {
    id: 'T006',
    name: 'EV Awareness Drive',
    category: 'Brand Awareness',
    thumbnail:
      'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?auto=format&fit=crop&q=80&w=600',
    channels: ['Facebook', 'Instagram', 'Google'],
    duration: '60 days',
    budgetRange: '₹1L - ₹3L',
    description: 'Educational campaign about EV benefits promoting the XUV400.',
  },
]

// Compliance Violations
export interface Violation {
  id: string
  partnerId: string
  partnerName: string
  type: 'Creative' | 'Messaging' | 'Pricing' | 'Brand Identity'
  severity: 'Critical' | 'High' | 'Medium' | 'Low'
  description: string
  date: string
  status: 'Open' | 'Investigating' | 'Resolved'
}

export const VIOLATIONS: Violation[] = [
  {
    id: 'V001',
    partnerId: 'MH201',
    partnerName: 'Pune Motors',
    type: 'Pricing',
    severity: 'Critical',
    description:
      'Unauthorized discount advertised on XUV700 exceeding brand guidelines.',
    date: '2023-10-24',
    status: 'Open',
  },
  {
    id: 'V002',
    partnerId: 'TN101',
    partnerName: 'Chennai Cars',
    type: 'Brand Identity',
    severity: 'High',
    description: 'Using outdated logo in social media creatives.',
    date: '2023-10-22',
    status: 'Investigating',
  },
  {
    id: 'V003',
    partnerId: 'DL302',
    partnerName: 'Delhi Auto Hub',
    type: 'Messaging',
    severity: 'Medium',
    description: 'Misleading claim about mileage in WhatsApp campaign.',
    date: '2023-10-20',
    status: 'Resolved',
  },
  {
    id: 'V004',
    partnerId: 'HR407',
    partnerName: 'Koncept Auto',
    type: 'Creative',
    severity: 'Low',
    description: 'Low resolution image used in banner ad.',
    date: '2023-10-18',
    status: 'Resolved',
  },
  {
    id: 'V005',
    partnerId: 'MH201',
    partnerName: 'Pune Motors',
    type: 'Brand Identity',
    severity: 'Medium',
    description: 'Incorrect font usage in email newsletter.',
    date: '2023-10-15',
    status: 'Resolved',
  },
  {
    id: 'V006',
    partnerId: 'TN101',
    partnerName: 'Chennai Cars',
    type: 'Pricing',
    severity: 'High',
    description: 'Hidden charges not disclosed in offer terms.',
    date: '2023-10-12',
    status: 'Resolved',
  },
]

// Brand Assets
export interface BrandAsset {
  id: string
  name: string
  category: 'Logo' | 'Color' | 'Typography' | 'Photography' | 'Template'
  thumbnail: string
  fileType: string
  size: string
  uploadedDate: string
  downloads: number
}

export const BRAND_ASSETS: BrandAsset[] = [
  {
    id: 'A001',
    name: 'Primary Brand Logo',
    category: 'Logo',
    thumbnail:
      'https://images.unsplash.com/photo-1626847037657-fd3622613ce3?auto=format&fit=crop&q=80&w=200',
    fileType: 'SVG',
    size: '24 KB',
    uploadedDate: '2023-01-15',
    downloads: 1245,
  },
  {
    id: 'A002',
    name: 'Brand Color Palette',
    category: 'Color',
    thumbnail:
      'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&q=80&w=200',
    fileType: 'PDF',
    size: '1.2 MB',
    uploadedDate: '2023-01-15',
    downloads: 850,
  },
  {
    id: 'A003',
    name: 'Primary Font Family',
    category: 'Typography',
    thumbnail:
      'https://images.unsplash.com/photo-1560416313-414b33c856a9?auto=format&fit=crop&q=80&w=200',
    fileType: 'ZIP',
    size: '4.5 MB',
    uploadedDate: '2023-01-20',
    downloads: 620,
  },
  {
    id: 'A004',
    name: 'SUV Lifestyle Shot 1',
    category: 'Photography',
    thumbnail:
      'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=200',
    fileType: 'JPG',
    size: '5.8 MB',
    uploadedDate: '2023-06-10',
    downloads: 340,
  },
  {
    id: 'A005',
    name: 'Social Media Post Template',
    category: 'Template',
    thumbnail:
      'https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&q=80&w=200',
    fileType: 'PSD',
    size: '25 MB',
    uploadedDate: '2023-08-05',
    downloads: 415,
  },
  {
    id: 'A006',
    name: 'Email Header Template',
    category: 'Template',
    thumbnail:
      'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=200',
    fileType: 'FIG',
    size: '8 MB',
    uploadedDate: '2023-08-12',
    downloads: 280,
  },
]

// Recent Activity
export const RECENT_ACTIVITY = [
  {
    id: '1',
    user: 'Mumbai Motors',
    action: 'launched a new campaign',
    target: 'Diwali Offer 2023',
    time: '2 hours ago',
    icon: LayoutTemplateIcon,
    color: 'bg-blue-100 text-blue-600',
  },
  {
    id: '2',
    user: 'System',
    action: 'detected a compliance violation',
    target: 'Pune Motors - Pricing',
    time: '4 hours ago',
    icon: AlertTriangleIcon,
    color: 'bg-red-100 text-red-600',
  },
  {
    id: '3',
    user: 'Delhi Auto Hub',
    action: 'onboarded as',
    target: 'Gold Tier Partner',
    time: 'Yesterday',
    icon: UsersIcon,
    color: 'bg-amber-100 text-amber-600',
  },
  {
    id: '4',
    user: 'Brand Team',
    action: 'uploaded new asset',
    target: 'XUV400 Product Shots',
    time: 'Yesterday',
    icon: ImageIcon,
    color: 'bg-purple-100 text-purple-600',
  },
  {
    id: '5',
    user: 'Chennai Cars',
    action: 'downloaded guidelines',
    target: 'Brand Identity v2.0',
    time: '2 days ago',
    icon: FileTextIcon,
    color: 'bg-green-100 text-green-600',
  },
]
